/**
 * AMS Toast System — zero external dependencies
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW IT WORKS:
 *   A tiny event-emitter (toastBus) connects the imperative API to React.
 *
 *   1. Any module calls  toast.success("msg") / toast.error("msg")
 *   2. That fires an event on toastBus
 *   3. <ToastContainer /> (mounted once in App.tsx) listens and adds the toast
 *      to its local state array
 *   4. Each toast auto-removes itself after `duration` ms
 *
 * USAGE:
 *   // Anywhere in the app:
 *   import { toast } from "../components/Toast";
 *   toast.success("Signed in successfully!");
 *   toast.error("Invalid credentials. Please try again.");
 *   toast.info("Saving offline…");
 *
 *   // In App.tsx (once):
 *   import { ToastContainer } from "../components/Toast";
 *   <ToastContainer />
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useEffect, useRef, useState } from "react";
import { colors, fw, fontFamily } from "../../theme";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type ToastType = "success" | "error" | "info" | "warning";

interface ToastItem {
  id:       number;
  type:     ToastType;
  message:  string;
  duration: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini event bus (no external dep, no React needed here)
// ─────────────────────────────────────────────────────────────────────────────

type Listener = (item: ToastItem) => void;
let _listeners: Listener[] = [];
let _idCounter = 0;

const toastBus = {
  subscribe:   (fn: Listener) => { _listeners.push(fn); },
  unsubscribe: (fn: Listener) => { _listeners = _listeners.filter((l) => l !== fn); },
  emit:        (item: ToastItem) => { _listeners.forEach((fn) => fn(item)); },
};

// ─────────────────────────────────────────────────────────────────────────────
// Public API  —  toast.success() / toast.error() / toast.info() / toast.warning()
// ─────────────────────────────────────────────────────────────────────────────

function show(type: ToastType, message: string, duration = 4000) {
  toastBus.emit({ id: ++_idCounter, type, message, duration });
}

export const toast = {
  success: (msg: string, duration?: number) => show("success", msg, duration),
  error:   (msg: string, duration?: number) => show("error",   msg, duration),
  info:    (msg: string, duration?: number) => show("info",    msg, duration),
  warning: (msg: string, duration?: number) => show("warning", msg, duration),
};

// ─────────────────────────────────────────────────────────────────────────────
// Visual config per type
// ─────────────────────────────────────────────────────────────────────────────

const CONFIG: Record<ToastType, { bg: string; border: string; icon: string; iconColor: string }> = {
  success: {
    bg:        "#f0fdf4",
    border:    "#16a34a",
    icon:      "✓",
    iconColor: "#16a34a",
  },
  error: {
    bg:        "#fff1f2",
    border:    colors.primary,
    icon:      "✕",
    iconColor: colors.primary,
  },
  info: {
    bg:        "#eff6ff",
    border:    "#3b82f6",
    icon:      "ℹ",
    iconColor: "#3b82f6",
  },
  warning: {
    bg:        "#fffbeb",
    border:    "#d97706",
    icon:      "⚠",
    iconColor: "#d97706",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Single Toast item component
// ─────────────────────────────────────────────────────────────────────────────

const TOAST_STYLES = `
  @keyframes ams-toast-in  { from{opacity:0;transform:translateX(60px)} to{opacity:1;transform:translateX(0)} }
  @keyframes ams-toast-out { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(60px)} }
  .ams-toast-enter { animation: ams-toast-in  0.3s cubic-bezier(.22,.68,0,1.2) both; }
  .ams-toast-exit  { animation: ams-toast-out 0.25s ease forwards; }
  .ams-toast-close:hover { opacity: 0.7 !important; }
`;

const SingleToast: React.FC<{
  item:     ToastItem;
  onRemove: (id: number) => void;
}> = ({ item, onRemove }) => {
  const [exiting, setExiting] = useState(false);
  const cfg = CONFIG[item.type];

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => onRemove(item.id), 270);
  };

  // Auto-dismiss after duration
  useEffect(() => {
    const t = setTimeout(dismiss, item.duration);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={exiting ? "ams-toast-exit" : "ams-toast-enter"}
      style={{
        display:      "flex",
        alignItems:   "flex-start",
        gap:          "12px",
        background:   cfg.bg,
        border:       `1px solid ${cfg.border}`,
        borderLeft:   `4px solid ${cfg.border}`,
        borderRadius: "10px",
        padding:      "14px 16px",
        minWidth:     "300px",
        maxWidth:     "400px",
        boxShadow:    "0 4px 20px rgba(0,0,0,0.12)",
        fontFamily,
        pointerEvents: "all",
      }}
    >
      {/* Icon */}
      <div
        style={{
          width:           "24px",
          height:          "24px",
          borderRadius:    "50%",
          background:      cfg.border,
          color:           "#fff",
          display:         "flex",
          alignItems:      "center",
          justifyContent:  "center",
          fontSize:        "13px",
          fontWeight:      fw.bold,
          flexShrink:      0,
          marginTop:       "1px",
        }}
      >
        {cfg.icon}
      </div>

      {/* Message */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize:   "13px",
            fontWeight: fw.semibold,
            color:      "#1f2937",
            lineHeight: 1.5,
            fontFamily,
          }}
        >
          {item.message}
        </div>
      </div>

      {/* Close */}
      <button
        className="ams-toast-close"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: "none",
          border:     "none",
          cursor:     "pointer",
          color:      "#9ca3af",
          fontSize:   "16px",
          padding:    "0 0 0 4px",
          lineHeight: 1,
          flexShrink: 0,
          opacity:    0.6,
          transition: "opacity 0.15s",
        }}
      >
        ×
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ToastContainer — mount once in App.tsx
// ─────────────────────────────────────────────────────────────────────────────

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const stylesInjected = useRef(false);

  // Inject keyframes once
  if (!stylesInjected.current) {
    const style = document.createElement("style");
    style.textContent = TOAST_STYLES;
    document.head.appendChild(style);
    stylesInjected.current = true;
  }

  useEffect(() => {
    const handler: Listener = (item) => {
      setToasts((prev) => [...prev, item]);
    };
    toastBus.subscribe(handler);
    return () => toastBus.unsubscribe(handler);
  }, []);

  const remove = (id: number) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position:      "fixed",
        top:           "20px",
        right:         "20px",
        zIndex:        99999,
        display:       "flex",
        flexDirection: "column",
        gap:           "10px",
        pointerEvents: "none",   // container is click-through; items re-enable it
        alignItems:    "flex-end",
      }}
      aria-live="assertive"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <SingleToast key={t.id} item={t} onRemove={remove} />
      ))}
    </div>
  );
};
